import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Building2, User, Mail, Lock, Eye, EyeOff, Loader2, Globe, Search, X, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PhoneInput, PhoneValue, DEFAULT_PHONE, formatPhoneString } from '@/components/ui/PhoneInput';
import { COUNTRY_CODES, COUNTRIES_WITH_FLAGS, type CountryWithCca2 } from '@/lib/geoData';
import { extractErrorMessage } from '@/lib/errorMessage';
import { Floating } from '@/components/ui/Floating';

const INPUT_BASE = 'w-full h-11 pl-10 pr-4 rounded-lg border border-gray-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1] focus:border-transparent placeholder:text-gray-400 transition-all';

type CountryOption = CountryWithCca2; // label = "🇮🇳 India", value = "India"

// ── Searchable Country Dropdown ───────────────────────────────────────────────
function CountrySelect({
  options,
  value,
  onChange,
  loading,
  error,
}: {
  options: CountryOption[];
  value: string;
  onChange: (v: string) => void;
  loading: boolean;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selected = options.find(o => o.value === value);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 10);
    else setQuery('');
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={loading}
        onClick={() => !loading && setOpen(v => !v)}
        className={`w-full h-11 pl-10 pr-3 rounded-lg border text-sm text-left flex items-center justify-between gap-2 transition-all
          ${open ? 'ring-2 ring-[#6366F1] border-[#6366F1]/40' : 'border-gray-200'}
          ${loading ? 'bg-gray-50 cursor-not-allowed' : 'bg-white hover:border-[#6366F1]/40 cursor-pointer'}
          ${!value ? 'text-gray-400' : 'text-slate-800'}`}
      >
        {/* Globe icon positioned like Field icon */}
        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <span className="flex-1 truncate">
          {loading ? 'Loading countries…' : (selected?.label || 'Select Country')}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#6366F1]" />}
          {!loading && value && (
            <span
              role="button"
              onClick={e => { e.stopPropagation(); onChange(''); }}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      <Floating
        open={open}
        anchorRef={ref}
        onClose={() => setOpen(false)}
        className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
        style={{ maxHeight: '240px', display: 'flex', flexDirection: 'column' }}
      >
        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 flex-shrink-0">
          <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <input
            ref={searchRef}
            className="flex-1 text-sm bg-transparent outline-none text-slate-800 placeholder:text-gray-400"
            placeholder="Search country…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && filtered.length === 1) { onChange(filtered[0].value); setOpen(false); }
            }}
          />
          {query && <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>}
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-3 text-sm text-gray-400 text-center">No results</p>
          ) : filtered.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors
                ${opt.value === value ? 'bg-[#6366F1] text-white font-medium' : 'text-slate-700 hover:bg-indigo-50 hover:text-[#6366F1]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {options.length > 10 && (
          <div className="px-3 py-1.5 border-t border-gray-50 bg-gray-50 flex-shrink-0">
            <p className="text-xs text-gray-400">{filtered.length} of {options.length} countries</p>
          </div>
        )}
      </Floating>

      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────
function Field({ icon: Icon, label, error, children }: {
  icon: React.ElementType; label: string; error?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-normal text-slate-600 mb-1">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        {children}
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export function RegisterPage() {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  // Already logged in — redirect to dashboard
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const [form, setForm] = useState({ organizationName: '', name: '', email: '', password: '', confirmPassword: '', website: '' });
  const [phone, setPhone] = useState<PhoneValue>(DEFAULT_PHONE);
  const [country, setCountry] = useState('');
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Legal consent — must be explicitly checked before submit is enabled.
  // Legal grounds for storing/processing personal data on signup (both
  // GDPR Art. 6(1)(a) and India DPDP 2023) require verifiable consent
  // captured at the point of collection.
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Countries — static (148 entries from lib/geoData). Previously fetched
  // from restcountries.com which added 3rd-party SPOF on the signup flow.
  // IP-geo auto-detect still uses ipapi.co — that's a "nice to have" so a
  // failure just leaves the user picking manually.
  useEffect(() => {
    setCountryOptions(COUNTRIES_WITH_FLAGS);
    setLoadingCountries(false);

    // Best-effort IP geo-detect. If ipapi.co is rate-limiting or down,
    // user just selects manually — never blocks signup.
    (async () => {
      try {
        const geo = await fetch('https://ipapi.co/json/').then(r => r.json());
        if (geo?.country_code) {
          const match = COUNTRIES_WITH_FLAGS.find(
            o => o.cca2.toLowerCase() === geo.country_code.toLowerCase(),
          );
          if (match) {
            setCountry(match.value);
            const dialMatch = COUNTRY_CODES.find(
              c => c.country.toLowerCase() === match.value.toLowerCase(),
            );
            if (dialMatch) setPhone(p => ({ ...p, countryCode: dialMatch.code }));
          }
        }
      } catch {
        // silently fail — user selects manually
      }
    })();
  }, []);

  const set = (key: string, value: string) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: '' }));
  };

  // When country changes → sync phone code
  const handleCountryChange = (v: string) => {
    setCountry(v);
    if (errors.country) setErrors(e => ({ ...e, country: '' }));
    if (v) {
      const dialMatch = COUNTRY_CODES.find(c => c.country.toLowerCase() === v.toLowerCase());
      if (dialMatch) setPhone(p => ({ ...p, countryCode: dialMatch.code }));
    }
  };

  // When phone code changes → sync country
  const handlePhoneChange = (val: PhoneValue) => {
    setPhone(val);
    if (val.countryCode !== phone.countryCode) {
      const dialEntry = COUNTRY_CODES.find(c => c.code === val.countryCode);
      if (dialEntry) {
        const countryOpt = countryOptions.find(o => o.value.toLowerCase() === dialEntry.country.toLowerCase());
        if (countryOpt) {
          setCountry(countryOpt.value);
          if (errors.country) setErrors(e => ({ ...e, country: '' }));
        }
      }
    }
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.organizationName.trim()) e.organizationName = 'Organization name is required';
    if (!form.name.trim()) e.name = 'Full name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Invalid email address';
    if (!phone.number.trim()) e.phone = 'Phone number is required';
    if (!country) e.country = 'Please select your country';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    if (!form.confirmPassword) e.confirmPassword = 'Please confirm your password';
    else if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match';
    if (!acceptedTerms) e.acceptedTerms = 'You must agree to the Terms of Service and Privacy Policy';
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setIsLoading(true);
    setApiError('');
    try {
      await register({
        organizationName: form.organizationName.trim(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        phone: formatPhoneString(phone),
        country,
        website: form.website, // honeypot
      });
      localStorage.setItem('verifyEmail', form.email.trim().toLowerCase());
      navigate('/verify-email', { replace: true });
    } catch (err: any) {
      setApiError(extractErrorMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#6366F1] tracking-tight">BrixLead</h1>
          <p className="text-sm text-slate-500 mt-2">Create your organization account</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Get started for free</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Honeypot — off-screen, not focusable; only bots fill it. */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={form.website}
              onChange={e => set('website', e.target.value)}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            {/* 1. Organization Name */}
            <Field icon={Building2} label="Organization Name" error={errors.organizationName}>
              <input className={INPUT_BASE} placeholder="Acme Inc." value={form.organizationName}
                onChange={e => set('organizationName', e.target.value)} />
            </Field>

            {/* 2. Full Name */}
            <Field icon={User} label="Your Full Name" error={errors.name}>
              <input className={INPUT_BASE} placeholder="John Doe" value={form.name}
                onChange={e => set('name', e.target.value)} />
            </Field>

            {/* 3. Work Email */}
            <Field icon={Mail} label="Work Email" error={errors.email}>
              <input type="email" className={INPUT_BASE} placeholder="you@company.com" value={form.email}
                onChange={e => set('email', e.target.value)} />
            </Field>

            {/* 4. Phone */}
            <div>
              <label className="block text-xs font-normal text-slate-600 mb-1">Phone Number</label>
              <PhoneInput value={phone} onChange={handlePhoneChange} placeholder="Phone number" />
              {errors.phone && <p className="mt-1 text-xs text-red-500">{errors.phone}</p>}
            </div>

            {/* 5. Country */}
            <div>
              <label className="block text-xs font-normal text-slate-600 mb-1">Country <span className="text-red-500">*</span></label>
              <CountrySelect
                options={countryOptions}
                value={country}
                onChange={handleCountryChange}
                loading={loadingCountries}
                error={errors.country}
              />
            </div>

            {/* 6. Password */}
            <Field icon={Lock} label="Password" error={errors.password}>
              <input type={showPassword ? 'text' : 'password'} className={`${INPUT_BASE} pr-10`}
                placeholder="Min. 8 characters" value={form.password}
                onChange={e => set('password', e.target.value)} />
              <button type="button" onClick={() => setShowPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </Field>

            {/* 7. Confirm Password */}
            <Field icon={Lock} label="Confirm Password" error={errors.confirmPassword}>
              <input type={showConfirm ? 'text' : 'password'} className={`${INPUT_BASE} pr-10`}
                placeholder="Repeat your password" value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)} />
              <button type="button" onClick={() => setShowConfirm(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-slate-600">
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </Field>

            {apiError && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
                {apiError}
              </div>
            )}

            {/* 8. Legal consent — mandatory before Register enables. Links
                open in new tabs so the user doesn't lose the filled form. */}
            <div className="pt-1">
              <label className="flex items-start gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (errors.acceptedTerms && e.target.checked) {
                      setErrors((prev) => ({ ...prev, acceptedTerms: '' }));
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#6366F1] focus:ring-[#6366F1] cursor-pointer flex-shrink-0"
                />
                <span className="text-xs text-slate-600 leading-relaxed">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] font-medium hover:underline">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#6366F1] font-medium hover:underline">
                    Privacy Policy
                  </Link>
                  . Support responds within <b>48 hours</b>.
                </span>
              </label>
              {errors.acceptedTerms && (
                <p className="mt-1.5 text-xs text-red-600 ml-6">{errors.acceptedTerms}</p>
              )}
            </div>

            {/* 9. Submit — disabled while consent unchecked so the checkbox
                is the visible gate, not a validation error after submit. */}
            <button type="submit" disabled={isLoading || !acceptedTerms}
              className="w-full h-11 bg-[#6366F1] hover:bg-[#4F46E5] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Account
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#6366F1] font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
